import { useState, useRef, useCallback, useEffect } from "react";
import { AppHeader } from "@/components/app-header";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Slider } from "@/components/ui/slider";
import { VideoTimeline } from "@/components/video-timeline";
import { CompareVideoSlot, type YTPlayer } from "@/components/compare-video-slot";
import { CameraSlot } from "@/components/camera-slot";
import { VideoPickerDrawer, type VideoDescriptor } from "@/components/video-picker-drawer";
import { useCamera } from "@/hooks/use-camera";
import { useCanvasRecorder } from "@/hooks/use-canvas-recorder";
import { useSaveRecording } from "@/hooks/use-save-recording";
import { useAuth } from "@/hooks/use-auth";
import { useLocation } from "wouter";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { Input } from "@/components/ui/input";
import {
  Play,
  Pause,
  ChevronLeft,
  ChevronRight,
  AlignCenter,
  Check,
  Video,
  Camera,
  Circle,
  Square,
  Save,
  Loader2,
} from "lucide-react";

// Feature flag: set VITE_ENABLE_DANCE_ALONGSIDE=true to re-enable camera/recording mode
const DANCE_ALONGSIDE_ENABLED = import.meta.env.VITE_ENABLE_DANCE_ALONGSIDE === "true";

const speedOptions = [0.25, 0.5, 0.75, 1, 1.25, 1.5, 2];

function formatTime(s: number): string {
  if (!isFinite(s) || s < 0) return "0:00";
  const m = Math.floor(s / 60);
  const sec = Math.floor(s % 60);
  return `${m}:${sec.toString().padStart(2, "0")}`;
}

function formatOffset(offset: number): string {
  if (Math.abs(offset) < 0.005) return "In sync";
  // Show 3 decimal places so frame-level adjustments (0.033s) are visible
  const abs = Math.abs(offset).toFixed(3);
  return offset > 0 ? `B is ${abs}s ahead` : `B is ${abs}s behind`;
}

export default function ComparePage() {
  useEffect(() => { document.title = "Compare Videos – DanceMirror"; }, []);

  const auth = useAuth();
  const [, setLocation] = useLocation();
  const isPaid = ["monthly", "yearly", "lifetime"].includes(auth.user?.subscriptionTier ?? "");

  // Mode: two-video compare vs dance alongside (camera)
  type CompareMode = "videos" | "camera";
  const [mode, setMode] = useState<CompareMode>("videos");
  const [showModeModal, setShowModeModal] = useState(true);

  const { cameraActive, startCamera, stopCamera, error: cameraError, videoRef: cameraVideoRef } = useCamera();
  const [showPlayback, setShowPlayback] = useState(false);
  const [mirrorCamera, setMirrorCamera] = useState(true);
  const [showSaveForm, setShowSaveForm] = useState(false);
  const [saveTitle, setSaveTitle] = useState("");

  // Video slots
  const [slotA, setSlotA] = useState<VideoDescriptor>(null);
  const [slotB, setSlotB] = useState<VideoDescriptor>(null);

  // Per-slot options
  const [mirrorA, setMirrorA] = useState(false);
  const [mirrorB, setMirrorB] = useState(true);
  const [audioSlot, setAudioSlot] = useState<"A" | "B">("A");

  // Shared playback
  const [isPlaying, setIsPlaying] = useState(false);
  const [masterTime, setMasterTime] = useState(0);
  const [durationA, setDurationA] = useState(0);
  const [playbackSpeed, setPlaybackSpeed] = useState(1);

  // Offset: Video B plays at masterTime + offsetSeconds
  const [offsetSeconds, setOffsetSeconds] = useState(0);
  const offsetRef = useRef(0);
  const [isLineupMode, setIsLineupMode] = useState(false);

  // Nudge step selector
  type NudgeStep = "1f" | "0.1s" | "1s";
  const NUDGE_STEPS: { id: NudgeStep; label: string; value: number }[] = [
    { id: "1f",   label: "1 frame", value: 1 / 30 },
    { id: "0.1s", label: "0.1s",    value: 0.1    },
    { id: "1s",   label: "1s",      value: 1      },
  ];
  const [nudgeStep, setNudgeStep] = useState<NudgeStep>("0.1s");

  // Portrait detection
  const [isPortrait, setIsPortrait] = useState(
    () => typeof window !== "undefined" && window.innerHeight > window.innerWidth
  );
  useEffect(() => {
    const update = () => setIsPortrait(window.innerHeight > window.innerWidth);
    window.addEventListener("resize", update);
    window.addEventListener("orientationchange", update);
    return () => { window.removeEventListener("resize", update); window.removeEventListener("orientationchange", update); };
  }, []);

  // Picker
  const [pickerOpen, setPickerOpen] = useState<"A" | "B" | null>(null);

  // Video refs
  const videoRefA = useRef<HTMLVideoElement | null>(null);
  const videoRefB = useRef<HTMLVideoElement | null>(null);

  // Canvas composite recorder (reference + camera side-by-side)
  const { recordingState, recordingUrl, recordingBlob, startRecording, stopRecording, discardRecording } =
    useCanvasRecorder(videoRefA, cameraVideoRef, { mirrorReference: mirrorA, mirrorCamera });

  const { saveState, saveError, saveRecording, resetSave } = useSaveRecording();

  // Whether this reference video supports composite recording (YouTube iframes can't be canvas-captured)
  const canRecord = slotA?.type === "cloudflare" && cameraActive;
  const ytPlayerRefA = useRef<YTPlayer | null>(null);
  const ytPlayerRefB = useRef<YTPlayer | null>(null);
  const lastYtBSeekTime = useRef(0);
  const isPlayingRef = useRef(false);
  const masterTimeRef = useRef(0);

  // Keep refs in sync
  useEffect(() => { offsetRef.current = offsetSeconds; }, [offsetSeconds]);
  useEffect(() => { isPlayingRef.current = isPlaying; }, [isPlaying]);
  useEffect(() => { masterTimeRef.current = masterTime; }, [masterTime]);

  // Reset when slot A changes
  useEffect(() => {
    setIsPlaying(false);
    setMasterTime(0);
    setDurationA(0);
  }, [slotA]);

  // Reset when slot B changes
  useEffect(() => {
    setOffsetSeconds(0);
    offsetRef.current = 0;
  }, [slotB]);

  // --- Helpers to control video ---

  const seekVideoA = useCallback((time: number) => {
    if (videoRefA.current) {
      try { videoRefA.current.currentTime = time; } catch {}
    } else if (ytPlayerRefA.current) {
      try { ytPlayerRefA.current.seekTo(time, true); } catch {}
    }
    setMasterTime(time);
    masterTimeRef.current = time;
  }, []);

  const seekVideoB = useCallback((time: number) => {
    const clamped = Math.max(0, time);
    if (videoRefB.current) {
      try { videoRefB.current.currentTime = clamped; } catch {}
    } else if (ytPlayerRefB.current) {
      try { ytPlayerRefB.current.seekTo(clamped, true); } catch {}
      lastYtBSeekTime.current = Date.now();
    }
  }, []);

  const playBoth = useCallback(() => {
    // Read A's actual current position directly from the element, not the stale ref
    let currentA = masterTimeRef.current;
    if (videoRefA.current) {
      currentA = videoRefA.current.currentTime;
    } else if (ytPlayerRefA.current) {
      try { currentA = ytPlayerRefA.current.getCurrentTime(); } catch {}
    }

    // If A is at/past its end, restart from the beginning
    if (durationA > 0 && currentA >= durationA - 0.1) {
      currentA = 0;
      if (videoRefA.current) { try { videoRefA.current.currentTime = 0; } catch {} }
      else if (ytPlayerRefA.current) { try { ytPlayerRefA.current.seekTo(0, true); } catch {} }
    }

    // Sync master time state to A's actual position
    setMasterTime(currentA);
    masterTimeRef.current = currentA;

    // Start video A
    if (videoRefA.current) videoRefA.current.play().catch(() => {});
    else if (ytPlayerRefA.current) { try { ytPlayerRefA.current.playVideo(); } catch {} }

    // Slot B: only seek and play if in two-video mode
    if (videoRefB.current || ytPlayerRefB.current) {
      const targetB = currentA + offsetRef.current;
      seekVideoB(targetB);
      lastYtBSeekTime.current = Date.now();
      if (videoRefB.current) videoRefB.current.play().catch(() => {});
      else if (ytPlayerRefB.current) { try { ytPlayerRefB.current.playVideo(); } catch {} }
    }

    setIsPlaying(true);
  }, [seekVideoB, durationA]);

  const pauseBoth = useCallback(() => {
    if (videoRefA.current) videoRefA.current.pause();
    else if (ytPlayerRefA.current) { try { ytPlayerRefA.current.pauseVideo(); } catch {} }

    if (videoRefB.current) videoRefB.current.pause();
    else if (ytPlayerRefB.current) { try { ytPlayerRefB.current.pauseVideo(); } catch {} }

    setIsPlaying(false);
  }, []);

  const togglePlay = useCallback(() => {
    if (!slotA) return;
    if (isPlayingRef.current) pauseBoth();
    else playBoth();
  }, [slotA, pauseBoth, playBoth]);

  const skipTime = useCallback((delta: number) => {
    const newTime = Math.max(0, Math.min(masterTimeRef.current + delta, durationA || 9999));
    seekVideoA(newTime);
    seekVideoB(newTime + offsetRef.current);
  }, [seekVideoA, seekVideoB, durationA]);

  const handleSeek = useCallback((value: number[]) => {
    const time = value[0];
    seekVideoA(time);
    seekVideoB(time + offsetRef.current);
  }, [seekVideoA, seekVideoB]);

  const changeSpeed = useCallback((speed: number) => {
    setPlaybackSpeed(speed);
    if (videoRefA.current) videoRefA.current.playbackRate = speed;
    if (videoRefB.current) videoRefB.current.playbackRate = speed;
    if (ytPlayerRefA.current) { try { ytPlayerRefA.current.setPlaybackRate(speed); } catch {} }
    if (ytPlayerRefB.current) { try { ytPlayerRefB.current.setPlaybackRate(speed); } catch {} }
  }, []);

  // Drift correction for YouTube B only (HTML5 video stays in sync naturally;
  // seeking a playing <video> element causes re-decode stutter)
  useEffect(() => {
    if (!isPlaying || !ytPlayerRefB.current) return;
    const now = Date.now();
    if (now - lastYtBSeekTime.current < 2000) return; // cooldown after any seek
    if (now - lastYtBSeekTime.current < 300) return;  // debounce
    const targetB = masterTime + offsetRef.current;
    try {
      const actualB = ytPlayerRefB.current.getCurrentTime();
      if (Math.abs(actualB - targetB) > 1.0) {
        ytPlayerRefB.current.seekTo(Math.max(0, targetB), true);
        lastYtBSeekTime.current = now;
      }
    } catch {}
  }, [masterTime, isPlaying]);

  // --- Offset controls ---

  const applyOffset = useCallback((newOffset: number) => {
    setOffsetSeconds(newOffset);
    offsetRef.current = newOffset;
    const targetB = masterTimeRef.current + newOffset;
    seekVideoB(targetB);
  }, [seekVideoB]);

  const adjustOffset = useCallback((delta: number) => {
    applyOffset(offsetRef.current + delta);
  }, [applyOffset]);

  // --- Lineup Mode ---

  const enterLineupMode = useCallback(() => {
    pauseBoth();
    setIsLineupMode(true);
  }, [pauseBoth]);

  const exitLineupMode = useCallback(() => {
    setIsLineupMode(false);
  }, []);

  // --- Audio toggle ---

  const handleToggleAudio = useCallback((slot: "A" | "B") => {
    setAudioSlot(slot);
    // Mute/unmute HTML5 videos immediately
    if (videoRefA.current) videoRefA.current.muted = slot !== "A";
    if (videoRefB.current) videoRefB.current.muted = slot !== "B";
  }, []);

  // --- Picker select ---

  const handlePickVideo = useCallback((slot: "A" | "B", descriptor: VideoDescriptor) => {
    if (slot === "A") setSlotA(descriptor);
    else setSlotB(descriptor);
    pauseBoth();
  }, [pauseBoth]);

  const bothLoaded = mode === "videos" ? (!!slotA && !!slotB) : (!!slotA && cameraActive);

  const handleSetMode = useCallback((m: CompareMode) => {
    if (m === mode) return;
    pauseBoth();
    if (m === "videos") {
      stopCamera();
    } else {
      setSlotB(null);
    }
    setMode(m);
  }, [mode, pauseBoth, stopCamera]);

  useEffect(() => {
    if (auth.user === null) { setLocation("/"); return; }
    if (auth.user && !isPaid) { setLocation("/pricing"); }
  }, [auth.user, isPaid, setLocation]);

  if (!auth.user || !isPaid) return null;

  return (
    <div className="min-h-screen flex flex-col bg-background">
      <Dialog open={showModeModal} onOpenChange={setShowModeModal}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>What would you like to do?</DialogTitle>
            <DialogDescription>Choose a mode to get started.</DialogDescription>
          </DialogHeader>
          <div className={`grid ${DANCE_ALONGSIDE_ENABLED ? "grid-cols-2" : "grid-cols-1"} gap-3 mt-2`}>
            <button
              onClick={() => { setMode("videos"); setShowModeModal(false); }}
              className="flex flex-col items-center gap-3 rounded-xl border-2 border-border p-6 hover:border-primary hover:bg-accent transition-colors"
            >
              <Video className="h-8 w-8" />
              <span className="font-semibold text-sm text-center">Compare Videos</span>
              <span className="text-xs text-muted-foreground text-center">Play two videos side by side</span>
            </button>
            {DANCE_ALONGSIDE_ENABLED && (
              <button
                onClick={() => { setMode("camera"); setShowModeModal(false); }}
                className="flex flex-col items-center gap-3 rounded-xl border-2 border-border p-6 hover:border-primary hover:bg-accent transition-colors"
              >
                <Camera className="h-8 w-8" />
                <span className="font-semibold text-sm text-center">Dance Alongside</span>
                <span className="text-xs text-muted-foreground text-center">Mirror a video with your camera</span>
              </button>
            )}
          </div>
        </DialogContent>
      </Dialog>

      {/* Recording playback modal */}
      <Dialog open={showPlayback} onOpenChange={(open) => {
        if (!open && saveState !== "uploading" && saveState !== "processing") {
          setShowPlayback(false);
          setShowSaveForm(false);
          resetSave();
        }
      }}>
        <DialogContent className="sm:max-w-lg">
          <DialogHeader>
            <DialogTitle>Your Recording</DialogTitle>
            <DialogDescription>Watch back your dance to see what you can improve.</DialogDescription>
          </DialogHeader>
          <video src={recordingUrl ?? undefined} controls className="w-full rounded-lg" />

          {/* Save form */}
          {showSaveForm && saveState === "idle" && (
            <div className="flex gap-2 mt-1">
              <Input
                value={saveTitle}
                onChange={(e) => setSaveTitle(e.target.value)}
                placeholder="Recording title"
                className="flex-1"
                maxLength={255}
                autoFocus
              />
              <Button
                size="sm"
                onClick={() => {
                  if (!recordingBlob || !slotA?.savedVideoId) return;
                  saveRecording({
                    blob: recordingBlob,
                    title: saveTitle.trim() || `Practice – ${slotA?.title ?? "Untitled"}`,
                    savedVideoId: slotA.savedVideoId,
                  });
                }}
                disabled={!recordingBlob || !slotA?.savedVideoId}
              >
                Save
              </Button>
              <Button size="sm" variant="ghost" onClick={() => setShowSaveForm(false)}>Cancel</Button>
            </div>
          )}

          {/* Save progress */}
          {(saveState === "uploading" || saveState === "processing") && (
            <div className="flex items-center gap-2 text-sm text-muted-foreground mt-1">
              <Loader2 className="h-4 w-4 animate-spin shrink-0" />
              {saveState === "uploading" ? "Uploading…" : "Processing…"}
            </div>
          )}

          {saveState === "saved" && (
            <p className="text-sm text-green-600 mt-1 flex items-center gap-1">
              <Check className="h-4 w-4" /> Saved to your library.
            </p>
          )}

          {saveState === "error" && (
            <p className="text-sm text-destructive mt-1">{saveError}</p>
          )}

          <div className="flex justify-between mt-2">
            <a href={recordingUrl ?? "#"} download="dance-recording.webm">
              <Button variant="outline" size="sm">Download</Button>
            </a>
            <div className="flex gap-2">
              {isPaid && saveState === "idle" && !showSaveForm && (
                <Button
                  size="sm"
                  variant="secondary"
                  onClick={() => {
                    setSaveTitle(`Practice – ${slotA?.title ?? new Date().toLocaleDateString()}`);
                    setShowSaveForm(true);
                  }}
                >
                  <Save className="h-3 w-3 mr-1" /> Save to Library
                </Button>
              )}
              {saveState === "saved" ? (
                <Button size="sm" variant="ghost" onClick={() => {
                  discardRecording();
                  setShowPlayback(false);
                  setShowSaveForm(false);
                  resetSave();
                }}>
                  Close
                </Button>
              ) : (
                <Button
                  size="sm"
                  variant="ghost"
                  disabled={saveState === "uploading" || saveState === "processing"}
                  onClick={() => { discardRecording(); setShowPlayback(false); setShowSaveForm(false); resetSave(); }}
                >
                  Discard
                </Button>
              )}
            </div>
          </div>
        </DialogContent>
      </Dialog>

      <AppHeader />

      <div className="flex-1 flex flex-col">
        {/* Video area */}
        <div className={`flex flex-1 ${isPortrait ? "flex-col" : "flex-row"} gap-0`}>

          {/* Portrait + camera mode: camera on top (mirror at eye level), reference below */}
          {isPortrait && mode === "camera" && (
            <div className="flex-1 min-h-0 min-w-0">
              <CameraSlot
                fillHeight
                isMirrored={mirrorCamera}
                onToggleMirror={() => setMirrorCamera((v) => !v)}
                cameraActive={cameraActive}
                error={cameraError}
                videoRef={cameraVideoRef}
                onStartCamera={startCamera}
                onStopCamera={stopCamera}
              />
            </div>
          )}

          {/* Reference video (slot A) — always shown; fills half-height in portrait camera mode */}
          <div className={
            isPortrait && mode === "camera"
              ? "flex-1 min-h-0 min-w-0 border-t"
              : `${isPortrait ? "w-full" : "flex-1"} min-w-0`
          }>
            <CompareVideoSlot
              fillHeight={isPortrait && mode === "camera"}
              slotId="A"
              label="Reference"
              descriptor={slotA}
              isMirrored={mirrorA}
              isAudioActive={audioSlot === "A"}
              playbackSpeed={playbackSpeed}
              onVideoRef={(el) => { videoRefA.current = el; if (el) { el.muted = audioSlot !== "A"; } }}
              onYtPlayerReady={(p) => { ytPlayerRefA.current = p; }}
              onDurationChange={setDurationA}
              onTimeUpdate={(t) => { setMasterTime(t); masterTimeRef.current = t; }}
              onPlay={() => { setIsPlaying(true); isPlayingRef.current = true; }}
              onPause={() => { setIsPlaying(false); isPlayingRef.current = false; }}
              onPickVideo={() => setPickerOpen("A")}
              onToggleMirror={() => setMirrorA((v) => !v)}
              onToggleAudio={() => handleToggleAudio("A")}
            />
          </div>

          {/* Second slot — camera (landscape) or compare video; hidden in portrait+camera (camera already above) */}
          {!(isPortrait && mode === "camera") && (
            <div className={`${isPortrait ? "w-full" : "flex-1"} min-w-0`}>
              {mode === "camera" ? (
                <CameraSlot
                  isMirrored={mirrorCamera}
                  onToggleMirror={() => setMirrorCamera((v) => !v)}
                  cameraActive={cameraActive}
                  error={cameraError}
                  videoRef={cameraVideoRef}
                  onStartCamera={startCamera}
                  onStopCamera={stopCamera}
                />
              ) : (
                <CompareVideoSlot
                  slotId="B"
                  label="Your Video"
                  descriptor={slotB}
                  isMirrored={mirrorB}
                  isAudioActive={audioSlot === "B"}
                  playbackSpeed={playbackSpeed}
                  onVideoRef={(el) => { videoRefB.current = el; if (el) { el.muted = audioSlot !== "B"; } }}
                  onYtPlayerReady={(p) => { ytPlayerRefB.current = p; }}
                  onDurationChange={() => {}}
                  onTimeUpdate={() => {}}
                  onPlay={() => {}}
                  onPause={() => {}}
                  onPickVideo={() => setPickerOpen("B")}
                  onToggleMirror={() => setMirrorB((v) => !v)}
                  onToggleAudio={() => handleToggleAudio("B")}
                />
              )}
            </div>
          )}
        </div>

        {/* Controls bar */}
        <div className="border-t bg-card flex flex-col gap-0 shrink-0">
          {/* Full-width timeline (YouTube-style) */}
          <VideoTimeline
            currentTime={masterTime}
            duration={durationA}
            onSeek={handleSeek}
          />

          <div className="px-3 pb-3 flex flex-col gap-3">
          {/* Mode toggle + page label */}
          <div className="flex items-center gap-2 flex-wrap">
            <div className="flex rounded-md border overflow-hidden shrink-0">
              <button
                onClick={() => handleSetMode("videos")}
                className={`flex items-center gap-1.5 px-3 py-1 text-xs font-medium ${DANCE_ALONGSIDE_ENABLED ? "border-r" : ""} transition-colors ${
                  mode === "videos"
                    ? "bg-primary text-primary-foreground"
                    : "bg-card text-muted-foreground hover:bg-accent"
                }`}
              >
                <Video className="w-3.5 h-3.5" />
                Compare Videos
              </button>
              {DANCE_ALONGSIDE_ENABLED && (
                <button
                  onClick={() => handleSetMode("camera")}
                  className={`flex items-center gap-1.5 px-3 py-1 text-xs font-medium transition-colors ${
                    mode === "camera"
                      ? "bg-primary text-primary-foreground"
                      : "bg-card text-muted-foreground hover:bg-accent"
                  }`}
                >
                  <Camera className="w-3.5 h-3.5" />
                  Dance Alongside
                </button>
              )}
            </div>
            {DANCE_ALONGSIDE_ENABLED && (
              <span className="text-[10px] font-medium px-1.5 py-0.5 rounded bg-muted text-muted-foreground leading-none">Beta</span>
            )}
          </div>
          {/* Playback row */}
          <div className="flex items-center gap-2">
            <Button
              size="icon"
              variant="ghost"
              className="shrink-0"
              onClick={() => skipTime(-5)}
              disabled={!slotA}
            >
              <ChevronLeft className="w-5 h-5" />
            </Button>
            <Button
              size="icon"
              className="shrink-0"
              onClick={togglePlay}
              disabled={!slotA}
            >
              {isPlaying ? <Pause className="w-5 h-5" /> : <Play className="w-5 h-5" />}
            </Button>
            <Button
              size="icon"
              variant="ghost"
              className="shrink-0"
              onClick={() => skipTime(5)}
              disabled={!slotA}
            >
              <ChevronRight className="w-5 h-5" />
            </Button>

            <span className="text-xs text-muted-foreground shrink-0 tabular-nums">
              {formatTime(masterTime)} / {formatTime(durationA)}
            </span>

            <select
              value={playbackSpeed}
              onChange={(e) => changeSpeed(parseFloat(e.target.value))}
              className="text-xs bg-transparent border rounded px-1 py-0.5 h-7"
              disabled={!slotA}
            >
              {speedOptions.map((s) => (
                <option key={s} value={s}>{s}x</option>
              ))}
            </select>
          </div>

          {/* Recording controls — only in Dance Alongside mode (feature flagged) */}
          {DANCE_ALONGSIDE_ENABLED && mode === "camera" && (
            <div className="flex items-center gap-2">
              {recordingState === "idle" && (
                slotA?.type === "youtube" ? (
                  <TooltipProvider>
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <span>
                          <Button size="sm" variant="destructive" disabled>
                            <Circle className="h-3 w-3 fill-current mr-1" /> Record
                          </Button>
                        </span>
                      </TooltipTrigger>
                      <TooltipContent>
                        Side-by-side recording requires an uploaded video as the reference
                      </TooltipContent>
                    </Tooltip>
                  </TooltipProvider>
                ) : (
                  <Button size="sm" variant="destructive" onClick={startRecording} disabled={!canRecord}>
                    <Circle className="h-3 w-3 fill-current mr-1" /> Record
                  </Button>
                )
              )}
              {recordingState === "recording" && (
                <Button size="sm" variant="outline" onClick={stopRecording}>
                  <Square className="h-3 w-3 fill-current mr-1" /> Stop
                </Button>
              )}
              {recordingState === "stopped" && (
                <>
                  <Button size="sm" variant="secondary" onClick={() => {
                    setShowSaveForm(false);
                    resetSave();
                    setShowPlayback(true);
                  }}>
                    <Play className="h-3 w-3 mr-1" /> View Recording
                  </Button>
                  <Button size="sm" variant="ghost" onClick={discardRecording}>Discard</Button>
                </>
              )}
            </div>
          )}

          {/* Offset / Lineup row — only relevant when comparing two videos */}
          {mode === "videos" && (
            <div className="flex items-center gap-2 flex-wrap">
              <span className="text-xs text-muted-foreground shrink-0">Offset (B):</span>

              {/* Step selector */}
              <div className="flex rounded-md border overflow-hidden shrink-0">
                {NUDGE_STEPS.map((s) => (
                  <button
                    key={s.id}
                    onClick={() => setNudgeStep(s.id)}
                    className={`px-2 py-0.5 text-xs font-mono border-r last:border-r-0 transition-colors ${
                      nudgeStep === s.id
                        ? "bg-primary text-primary-foreground"
                        : "bg-card text-muted-foreground hover:bg-accent"
                    }`}
                  >
                    {s.label}
                  </button>
                ))}
              </div>

              {/* − / + as a joined button pair */}
              <div className="flex rounded-md border overflow-hidden shrink-0">
                <Button
                  variant="ghost"
                  size="sm"
                  className="h-7 px-3 text-sm font-mono rounded-none border-r"
                  onClick={() => adjustOffset(-(NUDGE_STEPS.find((s) => s.id === nudgeStep)!.value))}
                  disabled={!bothLoaded}
                >
                  −
                </Button>
                <Button
                  variant="ghost"
                  size="sm"
                  className="h-7 px-3 text-sm font-mono rounded-none"
                  onClick={() => adjustOffset(NUDGE_STEPS.find((s) => s.id === nudgeStep)!.value)}
                  disabled={!bothLoaded}
                >
                  +
                </Button>
              </div>

              <span className="text-xs text-muted-foreground tabular-nums shrink-0 min-w-[140px] text-center">
                {bothLoaded ? formatOffset(offsetSeconds) : "—"}
              </span>

              <Button
                variant={isLineupMode ? "default" : "outline"}
                size="sm"
                className="h-7 px-3 text-xs shrink-0 gap-1 ml-auto"
                onClick={isLineupMode ? exitLineupMode : enterLineupMode}
                disabled={!bothLoaded}
                title="Pause and use the slider to align B with A"
              >
                {isLineupMode
                  ? <><Check className="w-3.5 h-3.5" /> Done</>
                  : <><AlignCenter className="w-3.5 h-3.5" /> Lineup</>}
              </Button>
            </div>
          )}

          {/* Lineup slider — shown below when active */}
          {mode === "videos" && isLineupMode && (
            <div className="flex items-center gap-2 -mt-1">
              <span className="text-xs text-muted-foreground shrink-0">Scrub B:</span>
              <div className="flex-1 px-1">
                <Slider
                  value={[offsetSeconds]}
                  min={-60}
                  max={60}
                  step={0.033}
                  onValueChange={(v) => applyOffset(v[0])}
                />
              </div>
              <span className="text-xs text-muted-foreground shrink-0">±60s</span>
            </div>
          )}
          </div>
        </div>
      </div>

      {/* Video picker drawers */}
      <VideoPickerDrawer
        open={pickerOpen === "A"}
        onClose={() => setPickerOpen(null)}
        onSelect={(d) => handlePickVideo("A", d)}
        title="Pick Reference Video (A)"
      />
      <VideoPickerDrawer
        open={pickerOpen === "B"}
        onClose={() => setPickerOpen(null)}
        onSelect={(d) => handlePickVideo("B", d)}
        title="Pick Your Video (B)"
      />
    </div>
  );
}
